import { api } from "@/api";

const MAX_DIM = 2000;
const RESIZE_FROM_BYTES = 3 * 1024 * 1024;
const RETRY_WAITS = [2000, 5000, 12000];

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const waitOnline = () =>
  navigator.onLine ? Promise.resolve() : new Promise((r) => window.addEventListener("online", r, { once: true }));

export async function sha256(file) {
  const buf = await file.arrayBuffer();
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

// Big photos are reduced to 2000px on the phone (PNG keeps transparency) so uploads are fast
export async function shrinkIfHuge(file) {
  if (file.size < RESIZE_FROM_BYTES || !window.createImageBitmap) return file;
  try {
    const bmp = await createImageBitmap(file);
    const scale = MAX_DIM / Math.max(bmp.width, bmp.height);
    if (scale >= 1) { bmp.close && bmp.close(); return file; }
    const c = document.createElement("canvas");
    c.width = Math.round(bmp.width * scale);
    c.height = Math.round(bmp.height * scale);
    c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
    bmp.close && bmp.close();
    const blob = await new Promise((r) => c.toBlob(r, "image/png"));
    return blob ? new File([blob], file.name.replace(/\.[^.]+$/, "") + ".png", { type: "image/png" }) : file;
  } catch (e) {
    return file;
  }
}

const retryable = (e) => !e.response || e.response.status >= 500 || e.response.status === 429;

// Sends one sticker with automatic retries for temporary failures (network drop, server busy)
export async function uploadWithRetry(buildForm, onProgress, shouldStop) {
  for (let attempt = 0; ; attempt++) {
    await waitOnline();
    try {
      return await api.post("/stickers", buildForm(), {
        timeout: 180000,
        onUploadProgress: (ev) => ev.total && onProgress(Math.round((ev.loaded / ev.total) * 100)),
      });
    } catch (e) {
      if (!retryable(e) || attempt >= RETRY_WAITS.length || shouldStop()) throw e;
      onProgress(0, attempt + 1);
      await sleep(RETRY_WAITS[attempt]);
    }
  }
}
